import React, { ForwardedRef } from 'react';
import { useState, useRef } from "react";
import { IconSvg } from '@att-bit/duc.components.icon-svg';

export type TextFieldProps = {
    /** for aria-label */
    accessibility?: string,
    /** for auto complete */
    autocompleteOption?: string,
    /** To disable copy paste on the input field */
    disableCopyPaste?: boolean,
    /** sets "disabled" attirbute of the input */
    disabled?: boolean,
    /** determines if input is in error state and displays errorText */
    error?: boolean,
    /** sets error text value */
    errorText?: string,
    /** sets the hint text */
    hintText?: string,
    /** sets the initial input value */
    value?: string,
    /** sets the label text */
    labelText?: string,
    /** sets the max length for the text field */
    maxLength?: number,
    /** counts the number of characters entered in the text field */
    charCounter?: number,
    /** sets the name attribute of the input */
    name?: string,
    /** sets the placeholder attribute of the input)ariaDescribedBy?: string (sets the aria-describedby attribute of the input.
     * Id of description text element to be passed */
    placeHolder?: string,
    /** gives input show/hide password functionality where the toggle is the words "Show" and "Hide" */
    showHidePassword?: boolean,
    /** Sets "data-spi" attribute of the input */
    spi?: boolean,
    /** withTooltip?: boolean (use in conjunction with the options.tooltip prop) */
    tooltip?: React.ReactNode,
    /** sets input's "type" attribute, should accept most common values text | password */
    type?: TextFieldType,
    /** passing an event fired during onChange  */
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
    /** passing an event fired during onBlur */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    /** passing an event fired during onFocus */
    onFocus?: (e: any) => void;
    /** passing an event fired during onKeyDown */
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export enum TextFieldType {
  DATE = "date",
  EMAIL = "email",
  HIDDEN = "hidden",
  INTEGER = "integer",
  NUMBER = "number",
  NUMERIC_PASSWORD = "numeric",
  PASSWORD = "password",
  TEL = "tel",
  TEXT = "text",
}

export enum MASK_CHARACTER {
  DEFAULT = "\u2022",
}

export const TextField = React.forwardRef (({
  hintText,
  id,
  labelText,
  name,
  placeHolder,
  accessibility,
  autocompleteOption,
  error,
  errorText,
  maxLength,
  showHidePassword,
  spi=false,
  tooltip,
  charCounter,
  ...props
}: TextFieldProps &React .InputHTMLAttributes<HTMLInputElement>, ref: ForwardedRef<HTMLInputElement>) => {

  /* stateful items */
  const [showHint, setShowHint] = useState(hintText ? true : false);
  const [showPassword, setShowPassword] = useState(false);

  // METHODS #############################################################
    // istanbul ignore next
    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // istanbul ignore next
        props.onChange?.(event);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // istanbul ignore next
        if (
            (props.type === TextFieldType.NUMERIC_PASSWORD &&
                (event.which <= 47 || event.which >= 58) &&
                event.which !== 8 &&
                event.which !== 9
                && (event.which <= 95 || event.which >= 106) && event.which !== 37 && event.which !== 39 && event.which !== 46) ||
            (props.disableCopyPaste &&
                event.ctrlKey &&
                (event.keyCode === 88 || event.keyCode === 67 || event.keyCode === 86))
        ) {
            event.preventDefault();
        } else {
            props.onKeyDown?.(event);
        }
    };

    const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        console.log("onBlur: ", showHint);
        // istanbul ignore else
        if (hintText) {
            setShowHint(false);
        }
        // istanbul ignore next
        props.onBlur?.(event);
    };

    const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        console.log("onFocus: ", showHint);
        // istanbul ignore else
        if (hintText) {
            setShowHint(true);
        }
        // istanbul ignore next
        props.onFocus?.(event);
    };

    const handleShowHidePassword = () => {
    // setState(({ showPassword }) => ({ showPassword: !showPassword }));
        setShowPassword(!showPassword);
    };

    const decideInputType = () => {
        let inputType = props.type;
        switch (props.type) {
            case TextFieldType.NUMERIC_PASSWORD:
                if (showPassword && showHidePassword) {
                    inputType = TextFieldType.TEL;
                } else {
                    inputType = TextFieldType.PASSWORD;
                }
                break;
            case TextFieldType.PASSWORD:
                if (
                    (showPassword && showHidePassword) 
                ) {
                    inputType = TextFieldType.TEXT;
                }
                break;
        }
        return inputType!;
    }

    const prevent = (event: any) => {
        // istanbul ignore else
        if (props.disableCopyPaste) {
            event.preventDefault();
        }
    };

    // const mask = () => {
    //     return props.type === TextFieldType.NUMERIC_PASSWORD && showPassword === false
    //         ? props.value?.replace(/[0-9]/g, MASK_CHARACTER.DEFAULT)!
    //         : props.value;
    // }


    // const referenceState = () => {
    //     return props.type === TextFieldType.NUMERIC_PASSWORD && showPassword === false
    //         ?  mask(props.value)!
    //         : currentTextValue;
    // }


    // RENDER ##############################################################
    const labelClasses = [];
    const inputClasses = ["textfield"];
    if (error) {
        labelClasses.push("formfield-label error");
        inputClasses.push("error");
    }
    if (props.disabled) {
        labelClasses.push("disabled");
    }

    let errorInlineText: string = error
        ? (errorText as string)
        : (hintText as string);
    // if (errorInlineText && inLineHtmlParse) {
    //     errorInlineText = errorInlineText.replace(/ /g, "&nbsp;");
    // }
    return (
    <>
        <div className={`form-row rel `} data-testid={props['data-testid']}>
                {labelText && (
                    <div className="inline-flex">
                        <label
                            className={
                                "formfield-label font-bold color-gray-700 " +
                                (error ? "error" : "") +
                                (props.disabled ? "disabled" : "")
                            }
                            dangerouslySetInnerHTML={{ __html: labelText }}
                            htmlFor={id}
                        />
                        <span className={"hidden-spoken"}>
                            {error ? errorText : hintText}
                        </span>
                        {tooltip !== undefined && tooltip}
                    </div>
                )}
                {charCounter && (
          <span
            id={id}
            className={"color-gray-500 char-counter pad-b-xxs"}
            aria-live={maxLength - props.value.length <= 20 ? "assertive" : "polite"}
            aria-atomic={false}
            aria-label={props.value.length + " out of " + maxLength + " characters"}
          >
            {props.value.length + "/" + charCounter}
          </span>
        )}
                <input
                    {...props}
                    aria-label={labelText ? undefined: accessibility ? accessibility : undefined}
                    aria-describedby={
                        (hintText && !error) ||
                        (error && errorText)
                            ? `msg_${id}`
                            : undefined
                    }
                    aria-invalid={error === true ? error : undefined}
                    autoComplete={autocompleteOption}
                    className={inputClasses.join(" ")}
                    data-spi={spi}
                    disabled={props.disabled}
                    id={id}
                    maxLength={maxLength}
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    onFocus={onFocus}
                    onKeyDown={onKeyDown}
                    onPaste={prevent}
                    placeholder={placeHolder}
                    ref={ref}
                    type={decideInputType()}
                    value={props.value}
                    max={props.type === TextFieldType.DATE ? "2999-12-31" : ""}
                />
                {showHidePassword && (
                    <button
                        aria-label={showPassword ? "Hide Text" : "Show Text"}
                        className={ "showHidePasswordButton absolute  pad-xxs font-regular color-ui-black"}
                        onClick={handleShowHidePassword}
                        tabIndex={0}
                        type="button"
                    >
                        {!showPassword ? "Show" : "Hide"}
                    </button>
                )}                
                {hintText && !error && (
                    <div
                        className="formfield-msg font-regular"                       
                        id={`msg_${id}`}
                        tabIndex={showHint ? 0 : -1}
                        aria-label={hintText}
                    >                       
                        {hintText}
                    </div>
                )}
                {error && errorText && (
                    <div
                        className={"formfield-msg font-regular"}
                        // dangerouslySetInnerHTML={
                        //     inLineHtmlParse === true
                        //         ? { __html: errorInlineText }
                        //         : undefined
                        // }
                        id={`msg_${id}`}
                        role={"alert"}
                        tabIndex={showHint ? 0 : -1}
                    >
                        {/* {inLineHtmlParse === true ? null : errorInlineText} */}
                        {errorInlineText}
                    </div>
                )}
        </div>
      {/*place dynamic style jsx below*/}
      <style jsx>{`
        .showHidePasswordButton {
            top: 35px;
            background-color: transparent;
            border: none;
            right: 8px;
            font-size: 14px;
            font-weight: 500;
        }

        .showHidePasswordButton:focus{
            outline: 1px dotted currentColor;
            outline-offset: 3px;
        }

        .char-counter{
            font-size: 15px;
            float: right;
        }
      `}</style>
    </>
  );
});
